import React, { useState } from 'react';
import { Send, Loader, HelpCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { apiService } from '../services/api';

interface QAPair {
  id: string;
  question: string;
  answer: string;
  timestamp: Date;
  references?: string[];
  isLoading?: boolean;
  isExpanded?: boolean;
}

interface AskQuestionsInterfaceProps {
  onClose?: () => void;
}

const AskQuestionsInterface: React.FC<AskQuestionsInterfaceProps> = ({ onClose }) => {
  const [question, setQuestion] = useState('');
  const [qaPairs, setQaPairs] = useState<QAPair[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exampleQuestions = [
    "What's the best age to start orthodontic treatment?",
    "How effective is Invisalign for moderate crowding?",
    "What are the success rates for TADs?",
    "Compare functional appliances vs fixed appliances for Class II",
    "When should I consider orthognathic surgery?",
    "Best practices for treating severe crowding without extractions"
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || isLoading) return;

    const newQA: QAPair = {
      id: `qa-${Date.now()}`,
      question: question.trim(),
      answer: '',
      timestamp: new Date(),
      isLoading: true,
      isExpanded: true
    };

    setQaPairs(prev => [newQA, ...prev]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.askQuestion(question);
      
      // Update the QA pair with the answer
      setQaPairs(prev => prev.map(qa => 
        qa.id === newQA.id 
          ? { ...qa, answer: response.response || '', isLoading: false, references: response.metadata?.references }
          : qa
      ));
      
      // Clear the question input
      setQuestion('');
    } catch (err) {
      setError('Failed to get answer. Please try again.');
      // Remove the loading QA pair
      setQaPairs(prev => prev.filter(qa => qa.id !== newQA.id));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = (id: string) => {
    setQaPairs(prev => prev.map(qa => 
      qa.id === id ? { ...qa, isExpanded: !qa.isExpanded } : qa
    ));
  };

  const clearHistory = () => {
    if (window.confirm('Clear all questions and answers?')) {
      setQaPairs([]);
      setError(null);
    }
  };

  const selectExample = (example: string) => {
    setQuestion(example);
  };

  return (
    <div className="h-full flex flex-col bg-medical-50">
      {/* Header */}
      <div className="bg-white border-b border-medical-200 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center space-x-3 mb-4">
            <HelpCircle className="w-8 h-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-semibold text-medical-900">Ask Questions</h1>
              <p className="text-medical-600">Get evidence-based answers from peer-reviewed orthodontic literature</p>
            </div>
          </div>

          {/* Question Input Form */}
          <form onSubmit={handleSubmit} className="mt-6">
            <div className="flex space-x-3">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Type your orthodontic question..."
                disabled={isLoading}
                className="flex-1 px-4 py-3 border border-medical-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={!question.trim() || isLoading}
                className="medical-button px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Ask</span>
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Example Questions */}
          <div className="mt-4">
            <p className="text-sm text-medical-600 mb-2">Example questions:</p>
            <div className="flex flex-wrap gap-2">
              {exampleQuestions.slice(0, 3).map((example, index) => (
                <button
                  key={index}
                  onClick={() => selectExample(example)}
                  disabled={isLoading}
                  className="text-xs px-3 py-1.5 bg-medical-100 text-medical-700 rounded-full hover:bg-medical-200 transition-colors disabled:opacity-50"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-4xl mx-auto mt-4 px-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        </div>
      )}

      {/* Q&A History */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-4xl mx-auto">
          {qaPairs.length > 0 ? (
            <div className="space-y-4">
              {/* Clear History Button */}
              {qaPairs.length > 1 && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={clearHistory}
                    className="text-sm text-medical-600 hover:text-medical-700 flex items-center space-x-1"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Clear History</span>
                  </button>
                </div>
              )}

              {/* Q&A Pairs */}
              {qaPairs.map((qa) => (
                <div key={qa.id} className="bg-white rounded-lg shadow-sm border border-medical-200 overflow-hidden">
                  {/* Question Header */}
                  <button
                    onClick={() => toggleExpand(qa.id)}
                    className="w-full px-6 py-4 flex items-start justify-between hover:bg-medical-50 transition-colors"
                  >
                    <div className="flex items-start space-x-3 text-left">
                      <HelpCircle className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-medical-900">{qa.question}</p>
                        <p className="text-xs text-medical-500 mt-1">
                          {qa.timestamp.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {!qa.isLoading && (
                      qa.isExpanded ? 
                        <ChevronUp className="w-5 h-5 text-medical-400" /> : 
                        <ChevronDown className="w-5 h-5 text-medical-400" />
                    )}
                  </button>

                  {/* Answer Content */}
                  {(qa.isExpanded || qa.isLoading) && (
                    <div className="px-6 py-4 border-t border-medical-100">
                      {qa.isLoading ? (
                        <div className="flex items-center space-x-3 text-medical-600">
                          <Loader className="w-5 h-5 animate-spin" />
                          <span>Searching peer-reviewed literature...</span>
                        </div>
                      ) : (
                        <div className="prose prose-sm max-w-none">
                          <ReactMarkdown
                            components={{
                              h3: ({ children }: any) => <h3 className="text-base font-semibold mb-2 mt-4">{children}</h3>,
                              p: ({ children }: any) => <p className="mb-3 text-medical-700">{children}</p>,
                              ul: ({ children }: any) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                              ol: ({ children }: any) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                              li: ({ children }: any) => <li className="text-medical-700">{children}</li>,
                              strong: ({ children }: any) => <strong className="font-semibold text-medical-900">{children}</strong>,
                            }}
                          >
                            {qa.answer}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-medical-300 mx-auto mb-4" />
              <p className="text-lg text-medical-600">No questions asked yet</p>
              <p className="text-sm text-medical-500 mt-2">
                Type a question above or click an example to get started
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AskQuestionsInterface;